/**
 * 5-tier community advice: BUY · KEEP · SELL · AVOID · WAIT (FPL-Mate style).
 *
 * Each cast awards +1 reputation server-side. Unlike prediction conviction
 * votes, these do NOT bias the model — they're a pure community signal shown
 * on the Market page.
 */
import React, { useEffect, useState } from "react";
import { TrendUp, Shield, TrendDown, Prohibit, Hourglass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { castMarketAdvice } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const ADVICES = [
    {
        id: "BUY", icon: TrendUp, color: "#9EE8B4", label: "Buy",
        copy: "I'd transfer this player IN this week. Strong fixtures + form line up — get him before ownership floods.",
    },
    {
        id: "KEEP", icon: Shield, color: "#FFD180", label: "Keep",
        copy: "Already own him? Hold the position. Not a fresh buy this week, but no reason to move him out either.",
    },
    {
        id: "SELL", icon: TrendDown, color: "#FF6B6B", label: "Sell",
        copy: "Time to ship out. Fixtures softening, form dipping, or better alternatives at his price point.",
    },
    {
        id: "AVOID", icon: Prohibit, color: "#A1A1AA", label: "Avoid",
        copy: "Don't even consider buying. Either the underlying numbers are weak or the price/fixture combo is wrong.",
    },
    {
        id: "WAIT", icon: Hourglass, color: "#9DB7F5", label: "Wait",
        copy: "Promising but not yet. One more strong GW or a price drop and he's a buy — keep him on the watchlist.",
    },
];

export default function AdviceButtons({ playerId, advice, compact = false, onChange }) {
    const { user, login } = useAuth();
    const [busy, setBusy] = useState(false);
    const [local, setLocal] = useState(advice);
    useEffect(() => { setLocal(advice); }, [advice]);

    const counts = local?.counts || { BUY: 0, KEEP: 0, SELL: 0, AVOID: 0, WAIT: 0 };
    const total = local?.total || 0;
    const my = local?.my_advice;
    const consensus = local?.consensus;

    const handle = async (id) => {
        if (!user) {
            toast.error("Sign in to advise", {
                action: { label: "Sign in", onClick: login },
            });
            return;
        }
        if (busy) return;
        setBusy(true);
        try {
            const fresh = await castMarketAdvice(playerId, id);
            setLocal(fresh);
            onChange?.(fresh);
            if (fresh.awarded_reputation) toast.success("+1 reputation · thanks for advising");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Advice failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <TooltipProvider delayDuration={200}>
            <div
                className="flex items-center gap-1 flex-wrap"
                data-testid={`advice-buttons-${playerId}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex border border-white/15">
                    {ADVICES.map(({ id, icon: Icon, color, label, copy }) => {
                        const active = my === id;
                        const count = counts[id] || 0;
                        return (
                            <Tooltip key={id}>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => handle(id)}
                                        data-testid={`advice-${playerId}-${id.toLowerCase()}`}
                                        className={`flex items-center gap-1 px-1.5 py-1 transition-colors font-mono text-[10px] tabular-nums cursor-help ${
                                            active ? "" : "text-white/55 hover:text-white"
                                        }`}
                                        style={active ? { background: color, color: "#050505" } : {}}
                                    >
                                        <Icon size={11} weight="bold" />
                                        {!compact && <span>{count}</span>}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    className="max-w-[260px] bg-black border border-white/20 text-white rounded-none font-mono text-[11px] leading-snug tracking-normal px-3 py-2"
                                >
                                    <div className="font-display font-black uppercase text-[10px] mb-1" style={{ color }}>
                                        {label} · {count} vote{count === 1 ? "" : "s"}
                                    </div>
                                    <div className="text-white/80">{copy}</div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
                {!compact && (
                    <span className="font-mono text-[10px] text-white/50 tabular-nums">
                        {total > 0 ? `${total} advise` : "no advice"}
                    </span>
                )}
                {consensus && (
                    <span
                        className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5"
                        data-testid={`advice-consensus-${playerId}`}
                        style={{
                            background: ADVICES.find((a) => a.id === consensus)?.color + "22",
                            color: ADVICES.find((a) => a.id === consensus)?.color,
                        }}
                    >
                        → {consensus}
                    </span>
                )}
            </div>
        </TooltipProvider>
    );
}
